<template>
  <b-card no-body>
    <div
      v-if="onProgress"
      class="data-loading d-flex justify-content-center"
    >
      <b-spinner
        class="mt-1"
        label="Loading"
      />
    </div>
    <b-card-header>
      <!-- title and subtitle -->
      <b-row class="w-100">
        <b-col class="col-12">
          <b-row>
            <b-col class="col-12 col-md-7 justify-content-start">
              <b-card-title class="mb-1">
                Page View
              </b-card-title>
            </b-col>
            <b-col class="col-12 col-md-5 justify-content-end avg-stats">
              <card-analytics-avg-page-statistics
                :data-object="avgStatistics"
                :on-progress="avgStatisticsOnProgress"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <!--/ title and subtitle -->
    </b-card-header>

    <b-card-body>
      <vue-apex-charts
        ref="realtimeChart"
        type="area"
        height="300"
        :options="chartOptions"
        :series="series"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BCardTitle,
    BSpinner,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { getCoreApiByObjectId } from '@/coreApi/coreApiHelper'
import CardAnalyticsAvgPageStatistics from '@/views/card/card-analytic/CardAnalyticsAvgPageStatistics.vue'

// eslint-disable-next-line no-unused-vars,import/no-extraneous-dependencies
const moment = require('moment')

export default {
    components: {
        CardAnalyticsAvgPageStatistics,
        BRow,
        BCol,
        BCard,
        VueApexCharts,
        BCardHeader,
        BCardBody,
        BCardTitle,
        BSpinner,
    },
    props: {
        main: {
            type: Object,
            default: () => {},
        },
        series1: {
            type: Object,
            default: () => {},
        },
        series2: {
            type: Object,
            default: () => {},
        },
        series3: {
            type: Object,
            default: () => {},
        },
        series1Name: {
            type: String,
            default: '7 days ago',
        },
        series2Name: {
            type: String,
            default: 'Yesterday',
        },
        series3Name: {
            type: String,
            default: 'Today',
        },
        onProgress: {
            type: Boolean,
            default: true,
        },
        skinMode: {
            type: String,
            default: 'light',
        },
        avgStatistics: {
            type: Object,
            default: () => {},
        },
        avgStatisticsOnProgress: {
            type: Boolean,
            default: true,
        },
        reRender: {
            type: Number,
            default: 0,
        },
    },
    data() {
        const series = [
            {
                name: this.series1Name,
                data: [],
            },
            {
                name: this.series2Name,
                data: [],
            },
            {
                name: this.series3Name,
                data: [],
            },
        ]

        const chartOptions = {
            theme: {
                mode: this.skinMode,
            },
            chart: {
                type: 'area',
                toolbar: {
                    show: true,
                },
                foreColor: this.skinMode === 'dark' ? '#f6f7f8' : '#373d3f',
                background: this.skinMode === 'dark' ? '#283046' : '#ffff',
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                curve: 'smooth',
            },
            legend: {
                show: true,
                position: 'top',
                horizontalAlign: 'left',
                fontSize: '12px',
                fontFamily: 'SF Pro Display',
            },
            grid: {
                xaxis: {
                    lines: {
                        show: false,
                    },
                },
                yaxis: {
                    lines: {
                        show: false,
                    },
                },
            },
            xaxis: {
                type: 'datetime',
                categories: [],
            },
            yaxis: {
                show: !this.isMobile(),
                // opposite: isRtl
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shadeIntensity: 1,
                    inverseColors: false,
                    opacityFrom: 0.7,
                    opacityTo: 0,
                    stops: [100, 100, 100, 0],
                },
            },
            tooltip: {
                theme: this.skinMode,
                x: {
                    format: 'HH:mm',
                },
            },
            colors: ['#CED4DC', '#ffe6e6', '#1973E8'],
        }

        const categories = []
        const seriesData = []
        const currentDate = new Date()
        for (let x = 0; x <= 23; x += 1) {
            for (let y = 0; y < 60; y += 1) {
                currentDate.setUTCHours(x, y, 0)
                categories.push(currentDate.getTime())
                seriesData.push([currentDate.getTime(), 0])
            }
        }

        series[0].data = seriesData
        series[1].data = seriesData
        series[2].data = seriesData
        chartOptions.xaxis.categories = categories

        return {
            series,
            chartOptions,
            avgPageDuration: 0,
            avgReadability: 0,
            avgRecirculation: 0,
            avgScrollPercentage: 0,
        }
    },
    watch: {
        // 7 Days Ago
        main() {
            this.processCurrentDay()
        },
        series1() {
            //  const latestObject = getCoreApiLatestObject(this.series1)
            const latestObject = getCoreApiByObjectId(this.series1, 'sevenDaysAgo')
            let sumSevenDaysAgo = 0
            if (
                latestObject
          && 'data' in latestObject
            ) {
                const currentDate = new Date()
                const seriesData = []
                const seriesDataAdded = {}
                latestObject.data.forEach(obj => {
                    const dataDate = new Date(obj.x)
                    currentDate.setUTCHours(dataDate.getHours(), dataDate.getMinutes(), 0)
                    if (!seriesDataAdded[currentDate.getTime()]) {
                        seriesDataAdded[currentDate.getTime()] = true
                        seriesData.push([currentDate.getTime(), obj.y])
                    }
                    // eslint-disable-next-line radix
                    sumSevenDaysAgo += parseInt(obj.y)
                })
                seriesData.sort((a, b) => a[0] - b[0])
                this.series[0].data = seriesData
                if (sumSevenDaysAgo) {
                    this.series[0].name = `${this.series1Name} ( ${sumSevenDaysAgo ? sumSevenDaysAgo.toLocaleString() : ''} )`
                } else {
                    this.series[0].name = this.series1Name
                }

                this.updateSeriesLine()
            } else {
                this.series[0].data = []
            }
        },
        // Yesterday
        series2() {
            //  const latestObject = getCoreApiLatestObject(this.series2)
            const latestObject = getCoreApiByObjectId(this.series2, 'yesterday')
            let sumYesterday = 0
            if (
                latestObject
          && 'data' in latestObject
            ) {
                const currentDate = new Date()
                const seriesData = []
                const seriesDataAdded = {}
                latestObject.data.forEach(obj => {
                    const dataDate = new Date(obj.x)
                    currentDate.setUTCHours(dataDate.getHours(), dataDate.getMinutes(), 0)
                    if (!seriesDataAdded[currentDate.getTime()]) {
                        seriesDataAdded[currentDate.getTime()] = true
                        seriesData.push([currentDate.getTime(), obj.y])
                    }
                    // eslint-disable-next-line radix
                    sumYesterday += parseInt(obj.y)
                })
                seriesData.sort((a, b) => a[0] - b[0])
                this.series[1].data = seriesData
                if (sumYesterday) {
                    this.series[1].name = `${this.series2Name} ( ${sumYesterday ? sumYesterday.toLocaleString() : ''} )`
                } else {
                    this.series[1].name = this.series2Name
                }
                this.updateSeriesLine()
            } else {
                this.series[1].data = []
            }
        },
        // Today
        series3() {
            this.processCurrentDay()
        },
        reRender() {
            this.updateSeriesLine()
        },
        skinMode() {
            this.$refs.realtimeChart.updateOptions({
                theme: {
                    mode: this.skinMode,
                },
                chart: {
                    foreColor: this.skinMode === 'dark' ? '#f6f7f8' : '#373d3f',
                    background: this.skinMode === 'dark' ? '#283046' : '#ffff',
                },
                tooltip: {
                    theme: this.skinMode,
                },
            })
        },
    },
    methods: {
        processCurrentDay() {
            //  const latestObject = getCoreApiLatestObject(this.series3)
            let minutelyPvCount = null
            const mainStatisticsObject = getCoreApiByObjectId(this.main, 'statistics')

            if (
                mainStatisticsObject
                && 'data' in mainStatisticsObject
                && mainStatisticsObject.data
                && mainStatisticsObject.data.at(-1)
            ) {
                const data = mainStatisticsObject.data.at(-1)

                if (data.minutelyPvCount) {
                    try {
                        minutelyPvCount = JSON.parse(data.minutelyPvCount)
                    } catch (e) {
                        minutelyPvCount = null
                    }
                }
            }

            const latestObject = getCoreApiByObjectId(this.series3, 'today')
            let sumToday = 0

            if (
                latestObject
                && 'data' in latestObject
            ) {
                const currentDate = new Date()
                const seriesData = []
                const seriesDataObject = {}

                if (minutelyPvCount) {
                    minutelyPvCount.forEach(obj => {
                        const dataDate = new Date(obj.x)
                        currentDate.setUTCHours(dataDate.getHours(), dataDate.getMinutes(), 0)
                        seriesDataObject[currentDate.getTime()] = obj.y
                    })

                    if (seriesDataObject) {
                        // eslint-disable-next-line radix
                        const largestKey = Math.max(...Object.keys(seriesDataObject).map(key => parseInt(key)))
                        if (largestKey && seriesDataObject[largestKey]) {
                            console.log('largestKey', new Date(largestKey))
                            delete seriesDataObject[largestKey]
                        }
                    }
                }

                latestObject.data.forEach(obj => {
                    const dataDate = new Date(obj.x)
                    currentDate.setUTCHours(dataDate.getHours(), dataDate.getMinutes(), 0)
                    let { y } = obj
                    if (seriesDataObject[currentDate.getTime()] && seriesDataObject[currentDate.getTime()] > y) {
                        y = seriesDataObject[currentDate.getTime()]
                    }
                    seriesDataObject[currentDate.getTime()] = y
                    // eslint-disable-next-line radix
                })

                // eslint-disable-next-line no-restricted-syntax,guard-for-in
                for (const x in seriesDataObject) {
                    // eslint-disable-next-line radix
                    seriesData.push([parseInt(x), seriesDataObject[x]])
                    sumToday += seriesDataObject[x]
                }

                seriesData.sort((a, b) => a[0] - b[0])

                this.series[2].data = seriesData

                if (sumToday) {
                    this.series[2].name = `${this.series3Name} ( ${sumToday ? sumToday.toLocaleString() : ''} )`
                } else {
                    this.series[2].name = this.series3Name
                }
                this.updateSeriesLine()
            } else {
                this.series[2].data = []
            }
        },
        updateSeriesLine() {
            this.$refs.realtimeChart.updateSeries(this.series, true, true)
        },
        isMobile() {
            return window.innerWidth < 768
        },
    },
}
</script>
